/**
 * activityGenerationAdapter — les catalogues, les corps de requête et les énoncés proposés de
 * « Générer une activité », chemin PARENT.
 *
 * Portage littéral de `tutor/students/GenerateActivityModal.js` (3 507 l.) et de ses deux
 * composants (`components/MaterialGenerationForm.js`, `components/MaterialResultsCards.js`).
 * Les corps de requête sont ceux du legacy, clé pour clé : `onSubmit` pour la génération en trois
 * cartes, `handleGenerateParcours` pour les activités similaires, `handleCreateActivityFromImage`
 * et `createActivity` pour les deux créations.
 *
 * Ce qui n'est pas ici est ce que le parent n'atteint pas : le multi-élèves (`forStudents`), le
 * mode programme de révision (`has_study_program` n'est pas exposé au parent) et l'ancienne
 * interface (masquée sur `isParentMode`).
 */
import {
    type UIActivityAnalysis,
    type UIActivityGenerationDraft,
    type UIActivityGenerationMissingField,
    type UIActivityGenerationOption,
    type UIActivitySkillPart,
    type UIGeneratedActivity,
    type UISubject,
} from "../../../ui/types"
import { toUISubject, type ApiSchoolSubject } from "../shared/schoolSubjectAdapter"

/** `common/chapters/ActivityLevelBadge.js` — `materialLevelsNames`. */
export const ACTIVITY_LEVEL_OPTIONS: UIActivityGenerationOption[] = [
    { value: "1", label: "Révision du cours" },
    { value: "2", label: "Entraînement" },
    { value: "3", label: "Approfondissement" },
]

/** `common/chapters/materialDuration.js` — `MATERIAL_DURATION_OPTIONS`. */
export const ACTIVITY_DURATION_OPTIONS: UIActivityGenerationOption[] = [
    { value: "5", label: "5 min" },
    { value: "10", label: "10 min" },
    { value: "15", label: "15 min" },
]

/** `materialDuration.getDefaultMaterialDuration` — le lycée travaille par tranches de 10 min. */
const LYCEE_CLASSES = ["2nde", "1ère", "Tle"]
export function defaultMaterialDuration(className: string | null | undefined): string {
    return className != null && LYCEE_CLASSES.includes(className) ? "10" : "5"
}

/**
 * Matière de repli quand l'enfant n'a aucune activité passée — `DEFAULT_SCHOOL_SUBJECT_ID` du
 * legacy, les maths étant de loin la matière la plus générée.
 */
export const DEFAULT_SCHOOL_SUBJECT_ID = "1"

type ApiFinishedActivity = { school_subject_id?: string | number | null; finished_at?: string | null }

/**
 * La matière de la dernière activité terminée de l'élève (`lastActivitySubjectId` du legacy) :
 * celle qu'ouvre le formulaire. `null` sans activité.
 */
export function lastFinishedActivitySubjectId(
    activities: ReadonlyArray<ApiFinishedActivity | null | undefined> | null | undefined
): string | number | null {
    const last = [...(activities ?? [])].sort(
        (a, b) => new Date(b?.finished_at ?? 0).getTime() - new Date(a?.finished_at ?? 0).getTime()
    )[0]
    return last?.school_subject_id ?? null
}

/** `GenerateActivityModal.classOptions` : les classes du formulaire, « Autre » retirée. */
export function toUIActivityClassOptions(classes: string[]): UIActivityGenerationOption[] {
    return classes.slice(0, -1).map((value) => ({ value, label: value }))
}

export type ApiSkillPart = { id?: string | number; position?: number; name?: string }

/** Les compétences d'un chapitre, triées par position comme `activeChapterSkillParts`. */
export function toUIActivitySkillParts(rawList: ApiSkillPart[] | null | undefined): UIActivitySkillPart[] {
    return (rawList ?? [])
        .map((row): UIActivitySkillPart | null =>
            row?.id == null || row.position == null || !row.name
                ? null
                : { id: row.id, position: row.position, name: row.name }
        )
        .filter((row): row is UIActivitySkillPart => row != null)
        .sort((a, b) => a.position - b.position)
}

/** La matière choisie dans le brouillon, retrouvée dans le catalogue en vigueur. */
export function findGenerationSubject(
    subjects: ApiSchoolSubject[] | null | undefined,
    subjectId: string | number | null | undefined
): ApiSchoolSubject | null {
    if (subjectId == null) return null
    return (subjects ?? []).find((subject) => String(subject?.id) === String(subjectId)) ?? null
}

/** « 4ème · Mathématiques » — le cadrage rappelé par la zone de dépôt et l'encart d'analyse. */
export function generationScopeLabel(
    draft: UIActivityGenerationDraft,
    subjects: ApiSchoolSubject[] | null | undefined
): string | null {
    const subject = findGenerationSubject(subjects, draft.subjectId)
    if (draft.className == null || subject?.name == null) return null
    return `${draft.className} · ${subject.name}`
}

export const MIN_INSTRUCTIONS_LENGTH = 5

/**
 * Ce qui manque pour partir, `null` quand rien ne manque. `GenerateActivityModal.isFormValid`,
 * branche manuelle : le chapitre suffit ; quand la classe et la matière n'ont aucun chapitre
 * validé, le backend en crée un à partir de ce que l'analyse a lu, et ce sont les instructions
 * qui deviennent obligatoires (5 caractères).
 */
export function missingGenerationField(
    draft: UIActivityGenerationDraft,
    chapterRequired: boolean
): UIActivityGenerationMissingField | null {
    if (draft.className == null) return "class"
    if (draft.subjectId == null) return "subject"
    if (chapterRequired) return draft.chapterId != null ? null : "chapter"
    return draft.instructions.length >= MIN_INSTRUCTIONS_LENGTH ? null : "instructions"
}

export type GenerationRequestContext = {
    studentId?: string | number | null
    /** `profile.interests` — envoyés seulement si « Personnaliser » est coché. */
    studentInterests?: string | null
    /** Identifiant de fournée : le backend s'en sert pour ne pas servir trois fois le même format. */
    batchId: string
    /** « Autres énoncés » : le backend saute sa réserve d'énoncés déjà générés. */
    forceGeneration?: boolean
}

/**
 * Le corps des trois appels `POST /api/v2/materials/generate` — `GenerateActivityModal.onSubmit`.
 * Les trois ne diffèrent que par `skills_part`, que le service pose lui-même.
 */
export function generateMaterialsBody(
    draft: UIActivityGenerationDraft,
    context: GenerationRequestContext
): Record<string, unknown> {
    const body: Record<string, unknown> = {
        class: draft.className,
        school_subject_id: draft.subjectId,
        instructions: draft.instructions,
        chapter_id: draft.chapterId ?? null,
        level: draft.level,
        duration: parseInt(draft.duration, 10),
    }
    if (context.studentId != null) body.student_ids = [context.studentId]
    if (draft.personalizeWithInterests && context.studentInterests) {
        body.student_interests = context.studentInterests
    }
    if (draft.skillPartPositions.length > 0) body.skill_part_positions = draft.skillPartPositions
    body.request_batch_id = context.batchId
    if (context.forceGeneration) body.force_generation = true
    return body
}

/**
 * Le corps commun des activités similaires — `handleGenerateParcours`. Chaque clé n'est posée que
 * lorsqu'elle a une valeur, exactement comme les étalements conditionnels du legacy.
 */
export function similarExercisesBody(
    level: string,
    draft: UIActivityGenerationDraft,
    context: GenerationRequestContext
): Record<string, unknown> {
    const body: Record<string, unknown> = {}
    const targetLevel = parseInt(level, 10)
    const targetDuration = parseInt(draft.duration, 10)
    if (targetLevel) body.target_level = targetLevel
    if (targetDuration) body.duration = targetDuration
    if (draft.instructions) body.instructions = draft.instructions
    if (draft.personalizeWithInterests && context.studentInterests) {
        body.student_interests = context.studentInterests
    }
    if (context.studentId != null) body.student_ids = [context.studentId]
    return body
}

/**
 * Le corps de « Créer l'activité » depuis un fichier lu — `handleCreateActivityFromImage`.
 * La matière et le chapitre choisis à la main sont renvoyés : le support avait figé les siens à
 * l'analyse, et le backend resynchronise avant de créer.
 */
export function createFromFileBody(
    materialId: string | number,
    draft: UIActivityGenerationDraft,
    materialTitle: string | null | undefined
): Record<string, unknown> {
    const body: Record<string, unknown> = {
        material_id: materialId,
        estimated_duration: parseInt(draft.duration, 10),
        material_title: materialTitle?.trim() || undefined,
        has_prerequisite_quiz: false,
    }
    if (draft.subjectId != null) body.school_subject_id = Number(draft.subjectId)
    if (draft.chapterId != null) body.chapter_id = Number(draft.chapterId)
    return body
}

export type ApiGeneratedMaterial = {
    id?: string | number
    sub_material_id?: string | number | null
    title?: string | null
    text?: string | null
    statement?: string | null
    body?: string | null
    chapter_name?: string | null
    chapter_model?: { name?: string | null } | null
    school_subject_id?: string | number | null
    school_subject?: ApiSchoolSubject | null
    material_key_skills?: string | null
    level?: string | number | null
    estimated_duration?: number | null
    num_clusters?: number | null
}

/** Le corps de « Créer » sur une carte de résultat — `createActivity`, branche élève unique. */
export function createResultBody(material: ApiGeneratedMaterial): Record<string, unknown> {
    return material.sub_material_id != null
        ? { sub_material_id: material.sub_material_id, has_prerequisite_quiz: false }
        : { material_id: material.id, has_prerequisite_quiz: false }
}

/**
 * Le libellé de variation d'une carte — `MaterialResultsCards.computeLegacyVariationLabel`,
 * mot pour mot. La génération en trois cartes n'en porte pas (le legacy y met « Type N », qui
 * dépend de la fournée entière et non de la carte).
 */
export function variationLabel(variation: string | null | undefined, numClusters?: number | null): string | undefined {
    switch (variation) {
        case "concept_review":
            return "Révision notions clés"
        case "method_review":
            return "Révision méthode"
        case "first_sub_exercise":
            return numClusters ? `Exercice en ${numClusters} étapes` : "Premier exercice"
        case "more_difficult":
            return "Plus difficile"
        case "same_difficulty":
            return "Même type"
        case "same_difficulty_diversified":
            return "Autre type"
        case "other_concepts":
            return "Autre compétence"
        default:
            return undefined
    }
}

/** Le libellé d'un type d'activité — `materialLevelsNames`, lu sur la valeur du backend. */
export function levelLabel(level: string | number | null | undefined): string | undefined {
    if (level == null) return undefined
    return ACTIVITY_LEVEL_OPTIONS.find((option) => option.value === String(level))?.label
}

export type ToUIGeneratedActivityOptions = {
    /** Le repli du legacy quand la réponse ne porte ni matière ni chapitre : le cadrage en cours. */
    fallbackSubject?: ApiSchoolSubject | null
    fallbackChapterName?: string | null
    variation?: string | null
    creation?: UIGeneratedActivity["creation"]
    quizEnabled?: boolean
    quizError?: boolean
}

/**
 * Un énoncé proposé → sa carte. Le contenu suit le legacy : `text`, sinon `statement` + `body`
 * (`MaterialResource` compose déjà `text` de cette façon quand il le peut).
 */
export function toUIGeneratedActivity(
    material: ApiGeneratedMaterial | null | undefined,
    options: ToUIGeneratedActivityOptions = {}
): Omit<UIGeneratedActivity, "id" | "status"> {
    const subjectSource = material?.school_subject ?? options.fallbackSubject ?? null
    const subject: UISubject | undefined = subjectSource != null ? toUISubject(subjectSource) : undefined
    const content =
        material?.text ??
        (material?.statement && material?.body ? `${material.statement}\n\n${material.body}` : undefined) ??
        undefined

    return {
        title: material?.title ?? undefined,
        chapterName:
            material?.chapter_name ?? material?.chapter_model?.name ?? options.fallbackChapterName ?? undefined,
        subject,
        keySkills: material?.material_key_skills ?? undefined,
        levelLabel: levelLabel(material?.level),
        variationLabel: variationLabel(options.variation, material?.num_clusters),
        content,
        creation: options.creation ?? "idle",
        quizEnabled: options.quizEnabled,
        quizError: options.quizError,
    }
}

export type ApiAnalyzedMaterial = {
    id?: string | number
    title?: string | null
    chapter_id?: string | number | null
    chapter_model?: { name?: string | null; parent_name?: string | null } | null
    chapter_name?: string | null
    structured_data?: {
        analysis?: { chapter_id?: string | number | null; confidence?: string | null; level?: string | number | null }
    } | null
}

/** Ce que l'analyse a proposé — `applyAnalysisToForm`, réduit à ce que l'encart du DS affiche. */
export function analysisSuggestion(material: ApiAnalyzedMaterial | null | undefined): {
    chapterId: string | number | null
    confidence: string
    level: string | null
    title: string | null
} {
    const analysis = material?.structured_data?.analysis
    return {
        chapterId: analysis?.chapter_id ?? null,
        confidence: analysis?.confidence ?? "low",
        level: analysis?.level != null ? String(analysis.level) : null,
        title: material?.title ?? null,
    }
}

export type ApiCurriculumSearchRow = {
    type?: string
    chapter?: {
        id?: string | number
        name?: string | null
        class?: string | null
        school_subject_id?: string | number | null
        school_subject?: { name?: string | null } | null
    } | null
}

/**
 * La proposition « Basculer » — le repli inter-classes du legacy (`/api/v1/curriculum/search`).
 * On préfère une ligne de chapitre, à défaut le chapitre parent d'une compétence ; une réponse
 * dans la classe en cours n'est pas une bascule et repart nulle.
 */
export type CrossClassMatch = {
    chapterId: string | number
    chapterName: string
    className: string
    /** La matière de l'autre classe : « Basculer » change le cadrage entier, pas le seul chapitre. */
    subjectId?: string | number
    subjectName?: string
}

export function crossClassMatch(
    rows: ApiCurriculumSearchRow[] | null | undefined,
    currentClassName: string | null | undefined
): CrossClassMatch | null {
    const list = rows ?? []
    const picked =
        list.find((row) => row?.type === "chapter" && row?.chapter) ??
        list.find((row) => row?.type === "skill_part" && row?.chapter)
    const chapter = picked?.chapter
    if (chapter?.id == null || !chapter.name || !chapter.class) return null
    if (currentClassName != null && chapter.class === currentClassName) return null
    return {
        chapterId: chapter.id,
        chapterName: chapter.name,
        className: chapter.class,
        subjectId: chapter.school_subject_id ?? undefined,
        subjectName: chapter.school_subject?.name ?? undefined,
    }
}

/** Le repli de l'encart d'analyse quand rien n'a été trouvé : ce que Sam a lu, et rien de plus. */
export function toUIActivityAnalysis(args: {
    status: UIActivityAnalysis["status"]
    material?: ApiAnalyzedMaterial | null
    selectedBySam?: boolean
    crossClass?: { className: string; subjectName?: string; chapterName: string }
    crossClassLoading?: boolean
}): UIActivityAnalysis {
    const suggestion = analysisSuggestion(args.material)
    return {
        status: args.status,
        readTitle: args.selectedBySam === true ? undefined : (suggestion.title ?? undefined),
        selectedBySam: args.selectedBySam,
        crossClass: args.crossClass,
        crossClassLoading: args.crossClassLoading,
    }
}

/** Jamais deux fois la même fournée — `GenerateActivityModal.onSubmit`. */
export function newBatchId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}
